import { Link } from "react-router-dom";
import { ClipboardPaste, LineChart } from "lucide-react";
import { useUiStore } from "@/stores/ui";
import { Card, eyebrow } from "@/components/ui/kit";
import { EXAMPLE_PINE } from "@/lib/new-backtest";

/**
 * Dashboard for someone with no backtests yet (design screen 01, "First run"):
 * one clear first action, two ways in, and the exchange left for later.
 */
export function FirstRun({ firstName }: { firstName?: string }) {
  const openNewBacktest = useUiStore((s) => s.openNewBacktest);

  const option = (icon: React.ReactNode, title: string, hint: string, onClick: () => void, dashed = false) => (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col gap-2 rounded-[12px] border bg-[var(--bg-card-elevated)] p-3 text-left hover:bg-[var(--bg-hover)] ${
        dashed ? "border-dashed border-[var(--border)]" : "border-[var(--primary)] shadow-[inset_0_0_0_1px_var(--primary)]"
      }`}
    >
      <span className="text-[var(--primary)]">{icon}</span>
      <span className="text-[13px] font-semibold text-[var(--text-primary)]">{title}</span>
      <span className="text-xs text-[var(--text-muted)]">{hint}</span>
    </button>
  );

  return (
    <Card className="mx-auto w-full max-w-[860px] p-5 md:p-6">
      <div className={eyebrow}>Welcome{firstName ? `, ${firstName}` : ""}</div>
      <h1 className="m-0 mb-2 mt-1.5 text-[26px] font-semibold leading-[1.15] tracking-[-0.02em] text-[var(--text-primary)] [text-wrap:balance] md:text-[30px]">
        See how your strategy would have traded <em className="not-italic text-[var(--primary)]">on years of real exchange data</em>.
      </h1>
      <p className="m-0 text-[13px] text-[var(--text-muted)]">
        Start with a backtest. It's free, trades nothing and needs no exchange account.
      </p>

      <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        {option(<LineChart size={18} />, "Try an example", "EMA Crossover, a simple trend follower. Results in seconds.", () =>
          openNewBacktest(EXAMPLE_PINE)
        )}
        {option(
          <ClipboardPaste size={18} />,
          "Paste your Pine Script",
          "From TradingView, v5 or v6. It must call strategy().",
          () => openNewBacktest(),
          true
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2.5 rounded-[10px] bg-[var(--primary-soft)] px-3 py-2.5 text-[13px] text-[var(--text-secondary)]">
        <span>
          <b className="font-semibold text-[var(--text-primary)]">How it works:</b> backtest, tweak the script and settings,
          then run it live on Binance, MEXC or Revolut X.
        </span>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="text-xs text-[var(--text-muted)]">Nothing is traded until you go live.</span>
        <Link to="/integrations/marketplaces/new" className="btn btn-ghost btn-sm ml-auto">
          Already trading? Add an exchange account
        </Link>
      </div>
    </Card>
  );
}
